import React, { useMemo } from "react"
import styled from "styled-components"

import {
  BANDWIDTH_UNIT,
  BUILD_TIME_UNIT,
  overageNote,
} from "../../../helpers/meteredBillingHelper"
import { toGigabytes } from "../../../helpers/numberHelpers"
import { hasPlanQuotaUnit, planQuotaLabel } from "../../../helpers/planHelpers"
import { quotaFor } from "../../../helpers/productKeys"
import { useUsage } from "../../../hooks"
import {
  CloudPlanItems,
  CmsPlanItems,
  Plan,
  QuotaLimit,
  QuotaType,
  TeamQuota,
} from "../../../interfaces"
import { t } from "../../../lib/i18n"
import { ServiceNameMap } from "../../../lib/service"
import { DetailButton } from "../cell/Cell"

// 単体追加した数量「(+N)」。引数は QuotaType（TeamQuota の行を持つ product_name）
// に限る。ProductName をそのままキーに使って `as keyof TeamQuota` で黙らせると、
// custom_domain / build_time が undefined になっても typecheck が通ってしまい、
// CLOUD の全行が空文字になっていた（#410）。キーの対応は quotaFor に寄せる。
//
// 足し込む先の上限（行に描いている includedQuota）が数値のときだけ出す。無制限・
// 未計測の行に付けると「無制限 (+3)」「— (+3)」になり、何に足したのか読めない。
const getPurchaseQuota = (
  productName: QuotaType,
  includedQuota: QuotaLimit,
  teamQuota?: TeamQuota,
): string => {
  if (!teamQuota) return ""
  if (includedQuota.kind !== "limited") return ""

  const quota = quotaFor(teamQuota, productName)
  if (!quota) return ""

  // 上限（included_quota）と同じ単位に揃える。bandwidth はバイトで来るので、
  // ここを通さないと同じ行が `5 (+42949672960)GB` になる。
  const purchased = toGigabytes(quota.purchasedQuota, productName)
  if (!purchased) return ""

  return ` (+${purchased.toLocaleString()})`
}

const CmsList = (
  plan: Plan,
  children: React.ReactNode,
  teamQuota?: TeamQuota,
): JSX.Element => {
  const { getAbtestLabel } = useUsage()
  const item = plan.items as CmsPlanItems

  return (
    <dl>
      <PlanRow>
        <PlanDt>{t("content_count")}</PlanDt>
        <PlanDd>{t("no_limited")}</PlanDd>
      </PlanRow>

      <PlanRow>
        <PlanDt>{t("membership_count")}</PlanDt>
        <PlanDd>
          {planQuotaLabel(item.membership.includedQuota)}
          {getPurchaseQuota(
            "membership",
            item.membership.includedQuota,
            teamQuota,
          )}
        </PlanDd>
      </PlanRow>

      <PlanRow>
        <PlanDt>{t("form_count")}</PlanDt>
        <PlanDd>
          {planQuotaLabel(item.form.includedQuota)}
          {getPurchaseQuota("form", item.form.includedQuota, teamQuota)}
        </PlanDd>
      </PlanRow>

      <PlanRow>
        <PlanDt>{t("bandwidth")}</PlanDt>
        <PlanDd>
          {planQuotaLabel(item.bandwidth.includedQuota)}
          {getPurchaseQuota(
            "bandwidth",
            item.bandwidth.includedQuota,
            teamQuota,
          )}
          {hasPlanQuotaUnit(item.bandwidth.includedQuota) && "GB"}
        </PlanDd>
      </PlanRow>

      <PlanRow>
        <PlanDt>{t("abtest_count")}</PlanDt>
        <PlanDd>{getAbtestLabel(plan)}</PlanDd>
      </PlanRow>

      {children}
    </dl>
  )
}

const CloudList = (
  plan: Plan,
  children: React.ReactNode,
  teamQuota?: TeamQuota,
): JSX.Element => {
  const item = plan.items as CloudPlanItems

  return (
    <dl>
      <PlanRow>
        <PlanDt>{t("site_count")}</PlanDt>
        <PlanDd>
          {/* サイト数に (+N) は出さない。案C v4 では site は全プラン price: 0
              （auth-api #1183 の config/plans.yml `v4: cloud:`）で、単体購入する
              商品ではないため。auth-api #1181 が商品自体は足すので、「商品が無い」
              が根拠ではない */}
          {planQuotaLabel(item.site.includedQuota)}
        </PlanDd>
      </PlanRow>

      <PlanRow>
        <PlanDt>{t("membership_count")}</PlanDt>
        <PlanDd>
          {planQuotaLabel(item.membership.includedQuota)}
          {getPurchaseQuota(
            "membership",
            item.membership.includedQuota,
            teamQuota,
          )}
        </PlanDd>
      </PlanRow>

      <PlanRow>
        <PlanDt>{t("custom_domain")}</PlanDt>
        <PlanDd>
          {planQuotaLabel(item.customDomain.includedQuota)}
          {getPurchaseQuota(
            "custom_domain",
            item.customDomain.includedQuota,
            teamQuota,
          )}
        </PlanDd>
      </PlanRow>

      <PlanRow>
        <PlanDt>{t("bandwidth")}</PlanDt>
        <PlanDd>
          {planQuotaLabel(item.bandwidth.includedQuota)}
          {getPurchaseQuota(
            "bandwidth",
            item.bandwidth.includedQuota,
            teamQuota,
          )}
          {hasPlanQuotaUnit(item.bandwidth.includedQuota) && BANDWIDTH_UNIT}
        </PlanDd>
      </PlanRow>

      {/*
        上限を超えた先に何が起きるかは、これまで画面に 1 行も出ていなかった
        （#418）。**free と有償で起きることが違います** —— free は従量課金が
        無く停止し、有償は超過分が課金されます。出さないと、free のユーザーが
        「超えたら課金される」と読み、有償のユーザーが「止まる」と読みます。
        どちらも問い合わせになります。

        **単価はここに書かないこと。** `item.bandwidth.price` を通します。
      */}
      {overageNote(item.bandwidth, BANDWIDTH_UNIT) && (
        <PlanRow>
          <PlanDt>{t("bandwidth_overage")}</PlanDt>
          <PlanDd>{overageNote(item.bandwidth, BANDWIDTH_UNIT)}</PlanDd>
        </PlanRow>
      )}

      <PlanRow>
        <PlanDt>{t("build_time")}</PlanDt>
        <PlanDd>
          {planQuotaLabel(item.buildTime.includedQuota)}
          {getPurchaseQuota(
            "build_time",
            item.buildTime.includedQuota,
            teamQuota,
          )}
          {hasPlanQuotaUnit(item.buildTime.includedQuota) && BUILD_TIME_UNIT}
        </PlanDd>
      </PlanRow>

      {overageNote(item.buildTime, BUILD_TIME_UNIT) && (
        <PlanRow>
          <PlanDt>{t("build_time_overage")}</PlanDt>
          <PlanDd>{overageNote(item.buildTime, BUILD_TIME_UNIT)}</PlanDd>
        </PlanRow>
      )}

      {children}
    </dl>
  )
}

type PlanContentProps = {
  plan: Plan
  teamQuota?: TeamQuota | null
  onClick?: (plan: Plan) => void
}

const PlanContent = (props: PlanContentProps): JSX.Element => {
  const onClick = (plan: Plan) => {
    if (!props.onClick) return
    props.onClick(plan)
  }

  const PlanPrice = useMemo(() => {
    return (
      <PlanRow>
        <PlanDt>{t("price")}</PlanDt>
        <PlanDd>
          ¥{props.plan.price.toLocaleString()}
          {props.onClick && (
            <DetailButton type="button" onClick={() => onClick(props.plan)}>
              {t("plan_detail")}
            </DetailButton>
          )}
        </PlanDd>
      </PlanRow>
    )
  }, [props.plan.price, props.onClick])

  const PlanItems = useMemo(() => {
    if (!props.teamQuota) {
      return <></>
    }

    if (props.plan.serviceName === ServiceNameMap.CMS) {
      return CmsList(props.plan, PlanPrice, props.teamQuota)
    } else {
      return CloudList(props.plan, PlanPrice, props.teamQuota)
    }
    // `props.teamQuota` は **プランが同じままチームだけ変わる**ときに差し替わる。
    // `AppHeader.openCms` / `openCloud` は SPA のナビゲーションを起こさずに
    // `setCurrentTeam` するので、プラン画面は開いたまま `currentTeam` だけ変わり、
    // `currentPlan`(selector) は planType が同じなら **同じ配列要素＝同じ参照**を返す。
    // 依存に入れないと、前のチームの単体追加分 (+N) を出したままになる(#416)。
    // `PlanPrice` も入れる。中身は `props.plan.price` / `props.onClick` から作られるので、
    // これを外すと価格だけ古いまま残せてしまう
  }, [props.plan, props.plan.serviceName, props.teamQuota, PlanPrice])

  return (
    <div>
      <dl>{PlanItems}</dl>
    </div>
  )
}

const PlanRow = styled.div`
  display: flex;
  justify-content: flex-start;
  align-items: center;
  font-style: normal;
  font-weight: 500;
  font-size: 14px;
  line-height: 140%;
  margin: 0 0 8px;
`

const PlanDt = styled.dt`
  color: ${(props) => props.theme.text.body2};
  min-width: 120px;
  margin: 0;
`

const PlanDd = styled.dd`
  color: ${(props) => props.theme.text.body1};
  display: flex;
  gap: 0 8px;
  margin: 0;
`

export default PlanContent
